<svelte:options
  customElement={{
    tag: "oc-search-field-v1",

    extend: window.__components.extend(),
    props: {
      overColor: { type: "Boolean", attribute: "over-color" },
      searchButtonType: { type: "String", attribute: "search-button-type" },
      clearButtonAriaLabel: { type: "String", attribute: "clear-button-aria-label" },
      searchButtonAriaLabel: { type: "String", attribute: "search-button-aria-label" },
    },
  }}
/>

<script lang="ts">
  import { useEventDispatcher } from "@otto-ec/otto-components-utils/use/event-dispatcher";
  import type { Events, Props } from "./SearchFieldV1.types.js";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  let {
    overColor = false,
    searchButtonType = "secondary",
    clearButtonAriaLabel = "Suchbegriff löschen",
    searchButtonAriaLabel = "Suche abschicken",
  }: Props = $props();

  const Host = $host();
  const dispatch = useEventDispatcher<Events>(Host);

  let searchFieldInput = $state<HTMLInputElement | null>();

  let value: string = $state("");

  const syncValue = (): void => {
    value = searchFieldInput?.value ?? "";

    dispatch(
      "oc-search-field-input",
      { searchTerm: searchFieldInput?.value },
      { cancelable: false },
    );
  };

  const clearInput = (): void => {
    if (!searchFieldInput) return;

    searchFieldInput.value = "";
    dispatch("oc-search-field-clear", null, { cancelable: false });
    searchFieldInput?.focus();

    syncValue();
  };

  const searchInput = (): void => {
    if (!searchFieldInput) return;

    dispatch(
      "oc-search-field-search",
      { searchTerm: searchFieldInput?.value },
      { cancelable: false },
    );
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (value !== "" && event.key === "Enter") {
      searchInput();
    }

    if (event.key === "Escape") {
      event.preventDefault();
      clearInput();
    }
  };

  const onslotchange = (): void => {
    searchFieldInput = Host.querySelector('input[type="search"]') as HTMLInputElement | null;
    syncValue();
  };
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="search-field"
  class:search-field--over-color={overColor}
  class:search-field-without-search-button={searchButtonType === "none"}
  oninput={syncValue}
  onkeydown={onKeyDown}
>
  <slot {onslotchange}></slot>
  {#if !value}
    <div class="search-field__icon">
      <oc-icon-v1 type="search"></oc-icon-v1>
    </div>
  {:else}
    <div class="search-field__icon-button-controls">
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <!-- svelte-ignore a11y_interactive_supports_focus -->
      <oc-icon-button-v3
        class="search-field__icon-button-controls__clear"
        role="button"
        icon="clear"
        variant="transparent"
        oc-aria-label={clearButtonAriaLabel}
        label={clearButtonAriaLabel}
        onclick={clearInput}
      ></oc-icon-button-v3>
      {#if searchButtonType !== "none"}
        <!-- svelte-ignore a11y_interactive_supports_focus -->
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <oc-icon-button-v3
          role="button"
          class="search-field__icon-button-controls__search"
          icon="search"
          variant={searchButtonType}
          oc-aria-label={searchButtonAriaLabel}
          label={searchButtonAriaLabel}
          onclick={searchInput}
        ></oc-icon-button-v3>
      {/if}
    </div>
  {/if}
</div>

<DefaultStyles />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  :global {
    :host {
      --oc-floating-focus-v1-radius: #{tokens.$oc-component-search-field-border-radius};
    }

    .search-field {
      position: relative;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: tokens.$oc-component-search-field-background-color;
      border-radius: tokens.$oc-component-search-field-border-radius;

      /*                                                                                                                        */
      &:focus-within {
        outline: 2px solid tokens.$oc-component-search-field-border-color-focused;
        background-color: tokens.$oc-component-search-field-background-color-focused;
      }

      &:hover:not(:focus-within) {
        cursor: text;
        outline: 1px solid tokens.$oc-component-search-field-border-color-hover;
      }

      & ::slotted(input[type="search"]) {
        all: unset;
        width: 100%;
        padding-top: tokens.$oc-component-search-field-spacing-y;
        padding-bottom: tokens.$oc-component-search-field-spacing-y;
        padding-left: tokens.$oc-component-search-field-spacing-left;
        padding-right: calc(#{tokens.$oc-component-search-field-spacing-right} + 80); /*   */
      }

      &-without-search-button ::slotted(input[type="search"]) {
        padding-top: tokens.$oc-component-search-field-spacing-y;
        padding-bottom: tokens.$oc-component-search-field-spacing-y;
        padding-left: tokens.$oc-component-search-field-spacing-left;
        padding-right: calc(#{tokens.$oc-component-search-field-spacing-right} + 40); /*   */
      }

      &--over-color {
        background-color: tokens.$oc-component-search-field-background-color-over-color;
      }

      /*                */
      &__icon {
        position: absolute;
        right: 0;
        line-height: 0;
        pointer-events: none;
        margin-right: calc(
          #{tokens.$oc-component-search-field-spacing-right} +
            #{tokens.$oc-component-search-field-gap-x} / 2
        );
        color: tokens.$oc-component-search-field-icon-color;
      }

      /*                    */
      &__icon-button-controls {
        position: absolute;
        right: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-right: tokens.$oc-component-search-field-spacing-right;
        gap: tokens.$oc-component-search-field-gap-x;
      }
    }
  }
</style>
